/**
 * Shape-only predicates for EVM values arriving from RPCs and upstream APIs. The server carries no
 * EVM library and this package stays light on dependencies, so nothing here decodes or checksums.
 */

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const BYTES32 = /^0x[0-9a-fA-F]{64}$/;
const HEX_QUANTITY = /^0x[0-9a-fA-F]+$/;
const HEX_DATA = /^0x[0-9a-fA-F]*$/;

/** Shape only: a mixed-case address is not held to its EIP-55 checksum. */
export const isEvmAddress = (value: string): boolean => EVM_ADDRESS.test(value);

/** A transaction or block hash, or one 32-byte ABI word. */
export const isBytes32Hex = (value: string): boolean => BYTES32.test(value);

/** At least one digit, as a JSON-RPC quantity always has. */
export const isHexQuantity = (value: string): boolean => HEX_QUANTITY.test(value);

/** May be bare `0x`, as an `eth_call` result can be. */
export const isHexData = (value: string): boolean => HEX_DATA.test(value);

export function canonicalEvmAddressOrNull(value: string): string | null {
  const lowered = value.trim().toLowerCase();
  return isEvmAddress(lowered) ? lowered : null;
}

/** Lowercase, so no stored copy or comparison depends on a checksum it was never held to. */
export function canonicalEvmAddress(value: string): string {
  const canonical = canonicalEvmAddressOrNull(value);
  if (canonical === null) throw new TypeError(`not an EVM address: ${value}`);
  return canonical;
}

/** Chain ordering: block first, then log index within a block. */
export const byChainOrder = (
  a: { blockNumber: number; logIndex: number },
  b: { blockNumber: number; logIndex: number },
): number => a.blockNumber - b.blockNumber || a.logIndex - b.logIndex;

/** False when either side is not an address. */
export function sameEvmAddress(a: string, b: string): boolean {
  const key = canonicalEvmAddressOrNull(a);
  return key !== null && key === canonicalEvmAddressOrNull(b);
}
